'use client';

import { usePathname, useSearchParams } from 'next/navigation';
import { useQueryStates } from 'nuqs';
import { useRef, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';

import {
  ArticleBanner,
  UiArticleBannerBlog
} from '@/fo/ui/article/banner/blog';
import { UiArticleHeaderBlog } from '@/fo/ui/article/header/blog';
import {
  ArticleItem,
  DEFAULT_SORT_OPTION,
  FilterOption,
  SortOptionEnum,
  UiArticleListBase
} from '@/fo/ui/article/list/base';
import {
  RubricArticle,
  RubricItem,
  UiArticleSectionTopFive
} from '@/fo/ui/article/section/top-five';
import { CategoryButton } from '@/fo/ui/category/button/base';
import { UiCategoryCarouselBase } from '@/fo/ui/category/carousel/base';
import { UiCategoryCarouselChip } from '@/fo/ui/category/carousel/chip';
import { UiModHeadFixed } from '@/fo/ui/mod/head/fixed';
import { utilScrollToElement } from '@/fo/util/scroll-to-element';

import { getPosts } from '../../api/getPosts';
import { topicsUrlMap } from '../../consts';
import { blogSearchParams } from '../../helpers/searchParams';
import { useStickyVisibility } from '../../hooks/useStickyVisibility';
import { BlogPageTopic, BlogPageType } from '../../types';

export interface PageBlogClientProps {
  bannerList: ArticleBanner[];
  categoriesList: CategoryButton[];
  postsList: ArticleItem[];
  totalPages: number;
  topicArticles: RubricItem[];
  anotherPost: RubricArticle | null;
  pageType: BlogPageType;
  topicKey: BlogPageTopic;
  topicTitle: string;
  topicDescription?: string;
  allPostsLabel: string;
  readAnotherPostLabel: string;
  filters?: FilterOption[];
}

export function PageBlogClient({
  bannerList,
  categoriesList,
  postsList,
  totalPages: initialTotalPages,
  topicArticles,
  anotherPost,
  pageType,
  topicKey,
  topicTitle,
  topicDescription,
  allPostsLabel,
  readAnotherPostLabel,
  filters
}: PageBlogClientProps) {
  const [posts, setPosts] = useState<ArticleItem[]>(postsList);
  const [urlParams, setUrlParams] = useQueryStates(blogSearchParams);
  const [totalPages, setTotalPages] = useState(initialTotalPages);
  const [loadingPage, setLoadingPage] = useState(false);
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const listScrollAnchorRef = useRef<HTMLDivElement>(null);

  const { rootRef, showSticky, onInteraction } = useStickyVisibility();

  const categories = categoriesList.map((category) => ({
    ...category,
    active: urlParams.categories.includes(String(category.id))
  }));

  const selectedFilters =
    pageType !== BlogPageType.Article ? urlParams.categories : [];

  const fetchPosts = async (
    sort: SortOptionEnum,
    activeCategories: string[],
    query?: string
  ) => {
    const { posts: newPosts, totalPages: newTotalPages } = await getPosts({
      type: pageType,
      sort,
      categories: activeCategories,
      query,
      currentPage: 1
    });

    setPosts(newPosts);
    setTotalPages(newTotalPages);
  };

  const getPageHref = (targetPage: number) => {
    const params = new URLSearchParams(searchParams.toString());
    const n = targetPage >= 1 ? targetPage : 1;
    if (n <= 1) {
      params.delete('p');
    } else {
      params.set('p', String(n));
    }

    const query = params.toString();

    return query ? `${pathname}?${query}` : pathname;
  };

  const handlePageChange = async (page: number) => {
    setLoadingPage(true);

    await setUrlParams({ p: page > 1 ? page : null });

    const { posts: newPosts, totalPages: newTotalPages } = await getPosts({
      type: pageType,
      sort: urlParams.sort,
      categories: urlParams.categories,
      query: urlParams.query,
      currentPage: page
    });

    setPosts(newPosts);
    setTotalPages(newTotalPages);
    setLoadingPage(false);
    requestAnimationFrame(() => {
      utilScrollToElement(listScrollAnchorRef.current, { offset: 8 });
    });
  };

  const handleSortChange = async (value: SortOptionEnum) => {
    onInteraction();

    setUrlParams({
      sort: value === DEFAULT_SORT_OPTION ? null : value,
      categories: urlParams.categories.length > 0 ? urlParams.categories : null,
      p: null
    });

    await fetchPosts(value, urlParams.categories, urlParams.query);
  };

  const handleResetFilters = async () => {
    setUrlParams({
      categories: null,
      p: null
    });

    await fetchPosts(urlParams.sort, [], urlParams.query);
  };

  const handleCategoryClick = async (id?: string | number) => {
    if (!id) {
      return;
    }

    // Блокируем скрытие sticky элемента при фильтрации
    onInteraction();

    const categoryId = String(id);
    const isActive = urlParams.categories.includes(categoryId);

    let newCategories: string[];
    if (isActive) {
      newCategories = urlParams.categories.filter(
        (category) => category !== categoryId
      );
    } else {
      newCategories = [...urlParams.categories, categoryId];
    }

    setUrlParams({
      sort: urlParams.sort === DEFAULT_SORT_OPTION ? null : urlParams.sort,
      categories: newCategories.length > 0 ? newCategories : null,
      p: null
    });

    await fetchPosts(urlParams.sort, newCategories, urlParams.query);
  };

  const handleFilterChange = async (value: string[]) => {
    // Блокируем скрытие sticky элемента при фильтрации
    onInteraction();

    setUrlParams({
      categories: value.length > 0 ? value : null,
      p: null
    });

    await fetchPosts(urlParams.sort, value, urlParams.query);
  };

  const handleSearch = useDebouncedCallback(async (value: string) => {
    setUrlParams({
      query: value,
      p: null
    });

    await fetchPosts(urlParams.sort, urlParams.categories, value);
  }, 400);

  return (
    <div>
      <UiArticleHeaderBlog />
      <UiArticleBannerBlog
        banners={bannerList}
        isVideo={pageType === BlogPageType.Video}
      />

      <div
        ref={rootRef}
        className="my-12"
      >
        {pageType === BlogPageType.Article && (
          <>
            <UiCategoryCarouselBase
              items={categories}
              onClick={handleCategoryClick}
            />

            <UiModHeadFixed
              show={showSticky}
              positioning="fixed"
              order={1}
              onClick={() => {
                // Блокируем скрытие при клике внутри sticky элемента
                onInteraction();
              }}
            >
              <UiCategoryCarouselChip
                items={categories}
                onClick={handleCategoryClick}
                onReset={handleResetFilters}
              />
            </UiModHeadFixed>
          </>
        )}
      </div>

      <UiArticleListBase
        rootRef={rootRef}
        listScrollAnchorRef={totalPages > 1 ? listScrollAnchorRef : undefined}
        showSticky={showSticky}
        filters={filters}
        selectedFilters={selectedFilters}
        onFilterChange={handleFilterChange}
        onSortChange={handleSortChange}
        onSearch={handleSearch}
        initialQuery={urlParams.query}
        pagination={
          totalPages > 1
            ? {
                pageCount: totalPages,
                page: urlParams.p,
                onPageChange: handlePageChange,
                loading: loadingPage,
                getPageHref
              }
            : undefined
        }
        sort={urlParams.sort}
        items={posts}
        isVideo={pageType === BlogPageType.Video}
        content={
          <UiArticleSectionTopFive
            href={topicsUrlMap[topicKey]}
            title={topicTitle}
            rubrics={topicArticles}
            article={anotherPost}
            description={topicDescription}
            allPostsLabel={allPostsLabel}
            readAnotherPostLabel={readAnotherPostLabel}
          />
        }
      />
    </div>
  );
}
